import {Component, inject, Input} from '@angular/core';
import {Router, RouterLink, RouterLinkActive} from "@angular/router";
import {CommonModule} from '@angular/common';
import {KeycloakService} from "../../services/keycloak.service";
import {BrandingService} from "../../services/branding.service";
import {OidcSecurityService} from "angular-auth-oidc-client";
import {User, UserRole, UserStatus} from "../../interfaces/api-interfaces";
import {NotificationBellComponent} from "../notifications/notification-bell/notification-bell.component";
import {ViewMode, ViewModeService} from "../../services/view-mode.service";

@Component({
  selector: 'app-header',
    imports: [
        RouterLink,
        RouterLinkActive,
        CommonModule,
        NotificationBellComponent
    ],
  templateUrl: './header.html'
})
export class Header {
    @Input({required: true}) user: User = {
        organization: [] as string[],
        email: "",
        firstName: "",
        fullName: "",
        userId: "",
        keycloakId: "",
        language: "",
        lastName: "",
        roles: [],
        status: UserStatus.PENDING,
        timezone: "",
        username: ""
    } ;
    private readonly oidcSecurityService = inject(OidcSecurityService);
    private readonly router = inject(Router);
    protected readonly keycloakService = inject(KeycloakService);
    protected readonly brandingService = inject(BrandingService);
    protected readonly viewModeService = inject(ViewModeService);

    // Mobile drawer state
    protected mobileDrawerOpen = false;
    onLogout() {
        this.oidcSecurityService.logoff().subscribe((result) => {
        });
    }

    closeDrawer() {
        this.mobileDrawerOpen = false;
    }

    handleImageError(event: Event) {
        const img = event.target as HTMLImageElement;
        if (img) {
            img.classList.add('hidden');
            // Show the fallback initials div
            const fallbackDiv = img.nextElementSibling as HTMLElement;
            if (fallbackDiv) {
                fallbackDiv.classList.remove('hidden');
            }
        }
    }

    getUserInitials(user: User): string {
        if (user.firstName && user.lastName) {
            return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
        }
        // Fallback to displayUsername or email first character
        const fallbackName = user.displayUsername || user.email || 'U';
        return fallbackName.charAt(0).toUpperCase();
    }

    /**
     * Get the display name based on user preferences
     */
    getDisplayName(user: User): string {
        if (!user) return '';
        
        // Use display preference if available, otherwise default to using real name
        const useDisplayName = user.useDisplayName !== undefined ? user.useDisplayName : true;
        
        if (useDisplayName && user.displayUsername) {
            return user.displayUsername;
        }
        
        // Fallback to full name or construct from first/last name
        if (user.fullName) {
            return user.fullName;
        }
        
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        
        return user.displayUsername || user.email || 'User';
    }

    sortRoles(){
        return this.user.roles.filter(role => Object.values(UserRole).includes(role));
    }

    translateRole(role: UserRole): string {
        const translations: { [key in UserRole]: string } = {
            [UserRole.MASASANA]: 'Masasana',
            [UserRole.FREYMUT]: 'FreyMuT',
            [UserRole.DOZENT]: 'Dozent',
            [UserRole.TEILNEHMER]: 'Teilnehmer',
            [UserRole.ABSOLVENT]: 'Absolvent'
        };
        return translations[role] || role;
    }

    translateRoles(): string {
        return this.sortRoles().map(role => this.translateRole(role)).join(', ');
    }

    isTeilnehmer(): boolean {
        return this.keycloakService.currentUser()?.roles?.includes(UserRole.TEILNEHMER) ?? false;
    }

    isDozent(): boolean {
        return this.keycloakService.currentUser()?.roles?.includes(UserRole.DOZENT) ?? false;
    }

    isAdmin(): boolean {
        const roles = this.keycloakService.currentUser()?.roles ?? [];
        return roles.includes(UserRole.MASASANA) || roles.includes(UserRole.FREYMUT);
    }

    hasMultipleViewModes(): boolean {
        const count = [this.isAdmin(), this.isDozent(), this.isTeilnehmer()].filter(Boolean).length;
        return count >= 2;
    }

    get activeNavMode(): 'admin' | 'dozent' | 'teilnehmer' {
        if (this.hasMultipleViewModes()) return this.viewModeService.mode();
        if (this.isAdmin()) return 'admin';
        if (this.isDozent()) return 'dozent';
        return 'teilnehmer';
    }

    setViewMode(mode: ViewMode): void {
        this.viewModeService.setMode(mode);
        let targetRoute: string;
        if (mode === 'admin') targetRoute = '/teaching';
        else targetRoute = '/home';
        this.router.navigate([targetRoute]);
        this.mobileDrawerOpen = false;
    }
}
